import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { SingleImageUploadField } from '@/components/ImageUploadField';
import type { GoodsSkuRecord, GoodsSpecRecord } from '@/services/modules/goods';

interface EditableSpecRecord extends GoodsSpecRecord {
  uid: string;
  inputValue?: string;
}

interface SkuEditorProps {
  value?: GoodsSkuRecord[];
  onChange?: (value: GoodsSkuRecord[]) => void;
  specs?: GoodsSpecRecord[];
  onSpecsChange?: (value: GoodsSpecRecord[]) => void;
  basePrice?: number;
  maxAttrs?: number;
}

const createSpecUid = () =>
  `spec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptySpec = (name = ''): EditableSpecRecord => ({
  uid: createSpecUid(),
  name,
  values: [],
  inputValue: '',
});

const parseAttrsByKey = (key: string) =>
  key
    .split('||')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((result, segment) => {
      const separatorIndex = segment.indexOf(':');
      if (separatorIndex < 0) {
        return result;
      }

      const attrName = segment.slice(0, separatorIndex).trim();
      const attrValue = segment.slice(separatorIndex + 1).trim();
      if (!attrName) {
        return result;
      }

      result[attrName] = attrValue;
      return result;
    }, {});

type SpecValueRecord = Pick<GoodsSpecRecord, 'name' | 'values'>;

const normalizeSpecs = (specs: SpecValueRecord[]) =>
  specs
    .map((item) => ({
      name: String(item.name || '').trim(),
      values: Array.from(
        new Set(
          (item.values || [])
            .map((value) => String(value || '').trim())
            .filter(Boolean),
        ),
      ),
    }))
    .filter((item) => item.name || item.values.length > 0);

const areSpecsEqual = (left: GoodsSpecRecord[], right: GoodsSpecRecord[]) =>
  left.length === right.length &&
  left.every(
    (item, index) =>
      item.name === right[index]?.name &&
      item.values.length === right[index]?.values.length &&
      item.values.every(
        (value, valueIndex) => value === right[index]?.values[valueIndex],
      ),
  );

const buildCombos = (specs: GoodsSpecRecord[]) => {
  if (!specs.length) {
    return [] as Record<string, string>[];
  }

  return specs.reduce<Record<string, string>[]>(
    (result, spec) => {
      const nextList: Record<string, string>[] = [];
      result.forEach((currentAttrs) => {
        spec.values.forEach((value) => {
          nextList.push({
            ...currentAttrs,
            [spec.name]: value,
          });
        });
      });
      return nextList;
    },
    [{}],
  );
};

const SkuEditor = ({
  value,
  onChange,
  specs,
  onSpecsChange,
  basePrice = 0,
  maxAttrs = 2,
}: SkuEditorProps) => {
  const { message } = App.useApp();
  const [localSpecs, setLocalSpecs] = useState<EditableSpecRecord[]>([
    createEmptySpec('颜色'),
  ]);
  const [batchPrice, setBatchPrice] = useState<number | null>(null);
  const [batchStock, setBatchStock] = useState<number | null>(null);

  const rows = value || [];

  useEffect(() => {
    const nextSpecs = normalizeSpecs(specs || []);

    setLocalSpecs((previous) => {
      // onSpecsChange 的父级回显不应重建本地编辑态，否则会清空正在输入的
      // inputValue，并因为 uid 变化导致属性名称输入框丢失焦点。
      if (areSpecsEqual(normalizeSpecs(previous), nextSpecs)) {
        return previous;
      }

      if (nextSpecs.length) {
        return nextSpecs.map((item) => ({
          uid: createSpecUid(),
          name: item.name,
          values: [...item.values],
          inputValue: '',
        }));
      }

      return [createEmptySpec('颜色')];
    });
  }, [specs]);

  const specColumns = useMemo(
    () => localSpecs.map((item) => item.name.trim()).filter(Boolean),
    [localSpecs],
  );

  const emitSpecsChange = (nextSpecs: EditableSpecRecord[]) => {
    setLocalSpecs(nextSpecs);
    onSpecsChange?.(normalizeSpecs(nextSpecs));
  };

  const updateSpecRow = (
    specIndex: number,
    updater: (current: EditableSpecRecord) => EditableSpecRecord,
  ) => {
    const nextSpecs = localSpecs.map((item, index) =>
      index === specIndex ? updater(item) : item,
    );
    emitSpecsChange(nextSpecs);
  };

  const updateSkuRow = (rowKey: string, patch: Partial<GoodsSkuRecord>) => {
    const nextRows = rows.map((item) =>
      item.key === rowKey
        ? {
            ...item,
            ...patch,
          }
        : item,
    );
    onChange?.(nextRows);
  };

  const rebuildSkus = () => {
    const nextSpecs = normalizeSpecs(localSpecs);
    if (
      !nextSpecs.length ||
      nextSpecs.some((item) => item.values.length === 0)
    ) {
      message.warning('请先填写属性名并添加属性值');
      onChange?.([]);
      onSpecsChange?.(nextSpecs);
      return;
    }

    const combos = buildCombos(nextSpecs);
    const oldRowMap = new Map(rows.map((item) => [item.key, item]));

    // 生成 SKU 时尽量复用已有价格、库存和图片，避免用户刷新规格后重新录入。
    const nextRows = combos.map((attrs, index) => {
      const key = nextSpecs
        .map((item) => `${item.name}:${attrs[item.name] || ''}`)
        .join('||');
      const existedRow = oldRowMap.get(key);

      return {
        id: existedRow?.id,
        key,
        sku_name: nextSpecs.map((item) => attrs[item.name] || '').join(' / '),
        price: Number(existedRow?.price ?? basePrice),
        stock: Number(existedRow?.stock ?? 0),
        sort: index + 1,
        code: existedRow?.code || '',
        image: existedRow?.image || '',
      };
    });

    onSpecsChange?.(nextSpecs);
    onChange?.(nextRows);
    message.success('SKU 已刷新');
  };

  const columns: ColumnsType<GoodsSkuRecord> = [
    ...specColumns.map((columnName): ColumnsType<GoodsSkuRecord>[number] => ({
      title: columnName,
      dataIndex: columnName,
      width: 120,
      render: (_, record) => parseAttrsByKey(record.key)[columnName] || '-',
    })),
    {
      title: 'SKU 编码',
      dataIndex: 'code',
      width: 160,
      render: (value, record) => (
        <Input
          value={value}
          placeholder="可选"
          onChange={(event) => {
            updateSkuRow(record.key, {
              code: event.target.value,
            });
          }}
        />
      ),
    },
    {
      title: '价格',
      dataIndex: 'price',
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={value}
          onChange={(nextValue) => {
            updateSkuRow(record.key, {
              price: Number(nextValue || 0),
            });
          }}
        />
      ),
    },
    {
      title: '库存',
      dataIndex: 'stock',
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          precision={0}
          style={{ width: '100%' }}
          value={value}
          onChange={(nextValue) => {
            updateSkuRow(record.key, {
              stock: Number(nextValue || 0),
            });
          }}
        />
      ),
    },
    {
      title: 'SKU 图',
      dataIndex: 'image',
      width: 180,
      render: (value, record) => (
        <SingleImageUploadField
          compact
          hint=""
          value={value}
          onChange={(nextValue) => {
            updateSkuRow(record.key, {
              image: nextValue,
            });
          }}
        />
      ),
    },
  ];

  return (
    <div className="zhiht-sku-editor">
      <div className="zhiht-sku-specs">
        {localSpecs.map((spec, specIndex) => (
          <Card key={spec.uid} className="zhiht-sku-spec-card" size="small">
            <Space
              align="start"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <div style={{ flex: 1 }}>
                <Typography.Text strong>属性名称</Typography.Text>
                <Input
                  placeholder="属性名：颜色/尺码/款式"
                  style={{ marginTop: 8 }}
                  value={spec.name}
                  onChange={(event) => {
                    updateSpecRow(specIndex, (current) => ({
                      ...current,
                      name: event.target.value,
                    }));
                  }}
                />
              </div>
              <Button
                danger
                disabled={localSpecs.length === 1}
                icon={<DeleteOutlined />}
                onClick={() => {
                  const nextSpecs = localSpecs.filter(
                    (_, index) => index !== specIndex,
                  );
                  emitSpecsChange(
                    nextSpecs.length ? nextSpecs : [createEmptySpec()],
                  );
                }}
              >
                删除属性
              </Button>
            </Space>

            <div style={{ marginTop: 16 }}>
              <Typography.Text strong>属性值</Typography.Text>
              <div className="zhiht-sku-value-wrap">
                {spec.values.map((valueItem, valueIndex) => (
                  <div
                    className="zhiht-sku-value-chip"
                    key={`${spec.uid}-${valueItem}`}
                  >
                    <Tag>{valueItem}</Tag>
                    <Space size={4}>
                      <Button
                        size="small"
                        type="text"
                        icon={<ArrowUpOutlined />}
                        disabled={valueIndex === 0}
                        onClick={() => {
                          updateSpecRow(specIndex, (current) => {
                            const nextValues = current.values.slice();
                            [
                              nextValues[valueIndex - 1],
                              nextValues[valueIndex],
                            ] = [
                              nextValues[valueIndex],
                              nextValues[valueIndex - 1],
                            ];
                            return {
                              ...current,
                              values: nextValues,
                            };
                          });
                        }}
                      />
                      <Button
                        size="small"
                        type="text"
                        icon={<ArrowDownOutlined />}
                        disabled={valueIndex === spec.values.length - 1}
                        onClick={() => {
                          updateSpecRow(specIndex, (current) => {
                            const nextValues = current.values.slice();
                            [
                              nextValues[valueIndex + 1],
                              nextValues[valueIndex],
                            ] = [
                              nextValues[valueIndex],
                              nextValues[valueIndex + 1],
                            ];
                            return {
                              ...current,
                              values: nextValues,
                            };
                          });
                        }}
                      />
                      <Button
                        danger
                        size="small"
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          updateSpecRow(specIndex, (current) => ({
                            ...current,
                            values: current.values.filter(
                              (_, index) => index !== valueIndex,
                            ),
                          }));
                        }}
                      />
                    </Space>
                  </div>
                ))}
              </div>

              <Space style={{ marginTop: 12 }} wrap>
                <Input
                  placeholder="输入值后点击添加"
                  style={{ width: 240 }}
                  value={spec.inputValue}
                  onPressEnter={() => {
                    const nextValue = String(spec.inputValue || '').trim();
                    if (!nextValue) {
                      return;
                    }

                    updateSpecRow(specIndex, (current) => ({
                      ...current,
                      inputValue: '',
                      values: current.values.includes(nextValue)
                        ? current.values
                        : [...current.values, nextValue],
                    }));
                  }}
                  onChange={(event) => {
                    updateSpecRow(specIndex, (current) => ({
                      ...current,
                      inputValue: event.target.value,
                    }));
                  }}
                />
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const nextValue = String(spec.inputValue || '').trim();
                    if (!nextValue) {
                      return;
                    }

                    updateSpecRow(specIndex, (current) => ({
                      ...current,
                      inputValue: '',
                      values: current.values.includes(nextValue)
                        ? current.values
                        : [...current.values, nextValue],
                    }));
                  }}
                >
                  添加属性值
                </Button>
              </Space>
              <Typography.Text type="secondary">
                添加后可通过上下按钮调整顺序。
              </Typography.Text>
            </div>
          </Card>
        ))}
      </div>

      <Space style={{ marginTop: 16 }} wrap>
        <Button
          disabled={localSpecs.length >= maxAttrs}
          icon={<PlusOutlined />}
          onClick={() => {
            emitSpecsChange([...localSpecs, createEmptySpec()]);
          }}
        >
          新增属性（最多 {maxAttrs} 个）
        </Button>
        <Button
          type="primary"
          ghost
          icon={<ReloadOutlined />}
          onClick={rebuildSkus}
        >
          生成 / 刷新 SKU
        </Button>
      </Space>

      {rows.length ? (
        <Card className="zhiht-sku-batch-card" size="small">
          <Space wrap>
            <Typography.Text strong>批量设置</Typography.Text>
            <Space>
              <Typography.Text>价格</Typography.Text>
              <InputNumber
                min={0}
                precision={2}
                value={batchPrice}
                onChange={(nextValue) => {
                  setBatchPrice(
                    nextValue === null ? null : Number(nextValue || 0),
                  );
                }}
              />
              <Button
                onClick={() => {
                  if (batchPrice === null) {
                    message.info('请先填写批量价格');
                    return;
                  }

                  onChange?.(
                    rows.map((item) => ({
                      ...item,
                      price: batchPrice,
                    })),
                  );
                }}
              >
                应用到全部
              </Button>
            </Space>
            <Space>
              <Typography.Text>库存</Typography.Text>
              <InputNumber
                min={0}
                precision={0}
                value={batchStock}
                onChange={(nextValue) => {
                  setBatchStock(
                    nextValue === null ? null : Number(nextValue || 0),
                  );
                }}
              />
              <Button
                onClick={() => {
                  if (batchStock === null) {
                    message.info('请先填写批量库存');
                    return;
                  }

                  onChange?.(
                    rows.map((item) => ({
                      ...item,
                      stock: batchStock,
                    })),
                  );
                }}
              >
                应用到全部
              </Button>
            </Space>
          </Space>
        </Card>
      ) : null}

      {rows.length ? (
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 960 }}
          style={{ marginTop: 16 }}
        />
      ) : (
        <div className="zhiht-sku-empty">
          <Empty description="请先配置规格和值，再点击“生成 / 刷新 SKU”" />
        </div>
      )}
    </div>
  );
};

export default SkuEditor;
